import React, { useState, useEffect, useCallback } from 'react';
import { automationAPI } from '../../services/api';
import { toast } from '../../utils/toast';
import Icon from '../../ui/Icon';
import { formatInTz } from '../../utils/timezone';

const s = {
  header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 12 },
  title: { fontSize: 16, fontWeight: 800, color: '#1A202C' },
  sub: { fontSize: 12, color: '#718096', marginTop: 4 },
  btn: (v) => ({
    display: 'inline-flex', alignItems: 'center', gap: 6,
    padding: '8px 14px', borderRadius: 8, border: 'none', cursor: 'pointer',
    fontSize: 12, fontWeight: 700, fontFamily: 'inherit',
    background: v === 'primary' ? 'var(--accent)' : v === 'danger' ? '#FEF2F2' : v === 'success' ? '#16a34a' : '#F1F5F9',
    color: v === 'primary' || v === 'success' ? '#fff' : v === 'danger' ? '#EF4444' : '#374151',
    opacity: v === 'disabled' ? 0.6 : 1,
  }),
  tab: (active) => ({
    padding: '8px 14px', borderRadius: 8, border: 'none', cursor: 'pointer',
    fontSize: 12, fontWeight: 700, fontFamily: 'inherit',
    background: active ? 'var(--accent)' : '#F1F5F9',
    color: active ? '#fff' : '#374151',
  }),
  th: {
    fontSize: 11, fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase',
    letterSpacing: '0.07em', padding: '10px 14px', borderBottom: '1px solid #f1f5f9',
    background: '#F8FAFF', textAlign: 'left',
  },
  td: {
    fontSize: 13, color: '#374151', padding: '11px 14px',
    borderBottom: '1px solid #f1f5f9', verticalAlign: 'middle',
  },
  badge: (status) => {
    const map = {
      PENDING_APPROVAL: { bg: '#FEF3C7', color: '#92400E', label: 'Pending' },
      APPROVED: { bg: '#DBEAFE', color: '#1D4ED8', label: 'Approved' },
      EXECUTED: { bg: '#DCFCE7', color: '#166534', label: 'Executed' },
      REJECTED: { bg: '#FEE2E2', color: '#991B1B', label: 'Rejected' },
      EXECUTION_FAILED: { bg: '#FFEDD5', color: '#9A3412', label: 'Failed' },
      EXPIRED: { bg: '#F3F4F6', color: '#4B5563', label: 'Expired' },
    };
    const st = map[status] || { bg: '#F3F4F6', color: '#4B5563', label: status || '—' };
    return {
      display: 'inline-block', padding: '3px 10px', borderRadius: 999,
      fontSize: 11, fontWeight: 700, background: st.bg, color: st.color,
    };
  },
};

const STATUS_TABS = [
  { key: 'PENDING_APPROVAL', label: 'Pending' },
  { key: '', label: 'All' },
  { key: 'EXECUTED', label: 'Executed' },
  { key: 'REJECTED', label: 'Rejected' },
  { key: 'EXECUTION_FAILED', label: 'Failed' },
];

function fmtDate(iso) {
  if (!iso) return '—';
  try {
    return formatInTz(iso, {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit', hour12: true,
    });
  } catch {
    return String(iso);
  }
}

function formatAction(code) {
  return String(code || '').replace(/_/g, ' ');
}

function ConfirmDialog({ type, request, rejectReason, onCancel, onConfirm, busy }) {
  const isApprove = type === 'approve';
  const title = isApprove ? 'Approve & execute?' : 'Reject request?';
  const message = isApprove
    ? (
      <>
        Approve and execute <strong>{request.approvalNumber}</strong>?
        <br /><br />
        <strong>Action:</strong> {formatAction(request.actionCode)}
        <br /><br />
        This will change Microsoft 365.
      </>
    )
    : (
      <>
        Reject <strong>{request.approvalNumber}</strong>?
        <br /><br />
        No Microsoft 365 changes will be made.
        {rejectReason ? (
          <>
            <br /><br />
            <strong>Reason:</strong> {rejectReason}
          </>
        ) : null}
      </>
    );

  return (
    <div
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 1100,
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
      }}
      onClick={onCancel}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="confirm-dialog-title"
        style={{
          background: '#fff', borderRadius: 14, width: '100%', maxWidth: 440,
          padding: '24px 28px', boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div id="confirm-dialog-title" style={{ fontSize: 17, fontWeight: 800, color: '#1A202C', marginBottom: 12 }}>
          {title}
        </div>
        <p style={{ fontSize: 14, color: '#4B5563', lineHeight: 1.55, margin: '0 0 22px' }}>
          {message}
        </p>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
          <button type="button" style={s.btn()} onClick={onCancel} disabled={busy}>
            Cancel
          </button>
          <button
            type="button"
            disabled={busy}
            style={s.btn(busy ? 'disabled' : isApprove ? 'success' : 'danger')}
            onClick={onConfirm}
          >
            {busy ? 'Processing…' : isApprove ? 'Approve & Execute' : 'Confirm Reject'}
          </button>
        </div>
      </div>
    </div>
  );
}

function DetailModal({ request, onClose, onApprove, onReject, busy }) {
  const [rejectReason, setRejectReason] = useState('');
  const canAct = request?.status === 'PENDING_APPROVAL';

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div style={{ background: '#fff', borderRadius: 14, width: '100%', maxWidth: 640, maxHeight: '90vh', overflow: 'auto', padding: '24px 28px', boxShadow: '0 20px 60px rgba(0,0,0,0.15)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: '#1A202C' }}>{request.approvalNumber}</div>
            <div style={{ fontSize: 12, color: '#718096', marginTop: 4 }}>{formatAction(request.actionCode)}</div>
          </div>
          <button type="button" onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
            <Icon name="close" size={18} color="#9CA3AF" />
          </button>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16, fontSize: 13 }}>
          <div><strong>Status:</strong> <span style={s.badge(request.status)}>{request.status?.replace(/_/g, ' ')}</span></div>
          <div><strong>Channel:</strong> {request.channel || '—'}</div>
          <div><strong>Requester:</strong> {request.requesterName || '—'}</div>
          <div><strong>Email:</strong> {request.requesterEmail || request.requestedBy || '—'}</div>
          <div><strong>Created:</strong> {fmtDate(request.createdAt)}</div>
          <div><strong>Expires:</strong> {fmtDate(request.expiresAt)}</div>
          {request.approvedBy && <div><strong>Approved by:</strong> {request.approvedBy}</div>}
          {request.rejectedBy && <div><strong>Rejected by:</strong> {request.rejectedBy}</div>}
          {request.reason && <div style={{ gridColumn: '1 / -1' }}><strong>Reason:</strong> {request.reason}</div>}
          {request.rejectionReason && <div style={{ gridColumn: '1 / -1' }}><strong>Rejection reason:</strong> {request.rejectionReason}</div>}
          {request.executionError && <div style={{ gridColumn: '1 / -1', color: '#DC2626' }}><strong>Error:</strong> {request.executionError}</div>}
        </div>

        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', marginBottom: 8 }}>Parameters</div>
          <pre style={{
            background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: 8,
            padding: 12, fontSize: 12, overflow: 'auto', margin: 0,
          }}>
            {JSON.stringify(request.params || {}, null, 2)}
          </pre>
        </div>

        {canAct && (
          <>
            <p style={{ fontSize: 12, color: '#718096', marginBottom: 12 }}>
              Approve to run this action in Microsoft 365. Reject to cancel — no changes will be made.
            </p>
            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 11, fontWeight: 700, color: '#6B7280', display: 'block', marginBottom: 6 }}>
                Rejection reason (optional)
              </label>
              <textarea
                value={rejectReason}
                onChange={e => setRejectReason(e.target.value)}
                rows={2}
                style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid #E2E8F0', fontFamily: 'inherit', fontSize: 13, boxSizing: 'border-box' }}
                placeholder="Reason if rejecting…"
              />
            </div>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <button
                type="button"
                disabled={busy}
                style={s.btn(busy ? 'disabled' : 'success')}
                onClick={() => onApprove(request)}
              >
                {busy ? 'Processing…' : 'Approve & Execute'}
              </button>
              <button
                type="button"
                disabled={busy}
                style={s.btn(busy ? 'disabled' : 'danger')}
                onClick={() => onReject(request, rejectReason)}
              >
                Reject
              </button>
              <button type="button" style={s.btn()} onClick={onClose}>Close</button>
            </div>
          </>
        )}

        {!canAct && (
          <button type="button" style={s.btn()} onClick={onClose}>Close</button>
        )}
      </div>
    </div>
  );
}

export default function AutomationAdmin() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('PENDING_APPROVAL');
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);
  const [health, setHealth] = useState(null);
  const [confirm, setConfirm] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 100 };
      if (statusFilter) params.status = statusFilter;
      const data = await automationAPI.listAdminRequests(params);
      setRequests(data.requests || []);
    } catch (err) {
      toast(err.message || 'Failed to load approval requests', 'error');
      setRequests([]);
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    automationAPI.health().then(setHealth).catch(() => setHealth(null));
  }, []);

  const handleApprove = async (req) => {
    setBusy(true);
    try {
      await automationAPI.approveRequest(req.id);
      toast('Approved and executed successfully', 'success');
      setConfirm(null);
      setSelected(null);
      load();
    } catch (err) {
      toast(err.message || 'Approval failed', 'error');
      load();
    } finally {
      setBusy(false);
    }
  };

  const handleReject = async (req, reason) => {
    setBusy(true);
    try {
      await automationAPI.rejectRequest(req.id, reason);
      toast('Request rejected', 'success');
      setConfirm(null);
      setSelected(null);
      load();
    } catch (err) {
      toast(err.message || 'Rejection failed', 'error');
    } finally {
      setBusy(false);
    }
  };

  const requestApprove = (req) => {
    setConfirm({ type: 'approve', req });
  };

  const requestReject = (req, reason) => {
    setConfirm({ type: 'reject', req, reason });
  };

  const pendingCount = statusFilter === 'PENDING_APPROVAL' ? requests.length : null;

  return (
    <div>
      <div style={s.header}>
        <div>
          <div style={s.title}>M365 Automation — Approval Queue</div>
          <div style={s.sub}>
            Review and approve Microsoft 365 actions before they run.
            {health && (
              <span style={{ marginLeft: 8 }}>
                Entra: {health.entraConfigured ? '✓ connected' : '✗ not configured'}
                {' · '}
                Approval: {health.approvalRequired ? 'required' : 'off'}
              </span>
            )}
          </div>
        </div>
        <button type="button" style={s.btn('primary')} onClick={load} disabled={loading}>
          <Icon name="refresh" size={14} color="#fff" />
          Refresh
        </button>
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        {STATUS_TABS.map(tab => (
          <button
            key={tab.key || 'all'}
            type="button"
            style={s.tab(statusFilter === tab.key)}
            onClick={() => setStatusFilter(tab.key)}
          >
            {tab.label}
            {tab.key === 'PENDING_APPROVAL' && pendingCount != null ? ` (${pendingCount})` : ''}
          </button>
        ))}
      </div>

      <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #E2E8F0', overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#718096', fontSize: 13 }}>Loading…</div>
        ) : requests.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#718096', fontSize: 13 }}>
            No approval requests{statusFilter ? ` with status "${statusFilter.replace(/_/g, ' ')}"` : ''}.
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 900 }}>
              <thead>
                <tr>
                  <th style={s.th}>Reference</th>
                  <th style={s.th}>Action</th>
                  <th style={s.th}>Requester</th>
                  <th style={s.th}>Status</th>
                  <th style={s.th}>Channel</th>
                  <th style={s.th}>Created</th>
                  <th style={s.th}>Expires</th>
                  <th style={s.th}></th>
                </tr>
              </thead>
              <tbody>
                {requests.map(req => (
                  <tr key={req.id} style={{ cursor: 'pointer' }} onClick={() => setSelected(req)}>
                    <td style={{ ...s.td, fontWeight: 700, color: '#1A202C' }}>{req.approvalNumber}</td>
                    <td style={s.td}>{formatAction(req.actionCode)}</td>
                    <td style={s.td}>
                      <div>{req.requesterName || '—'}</div>
                      <div style={{ fontSize: 11, color: '#9CA3AF' }}>{req.requesterEmail || req.requestedBy}</div>
                    </td>
                    <td style={s.td}><span style={s.badge(req.status)}>{req.status?.replace(/_/g, ' ')}</span></td>
                    <td style={s.td}>{req.channel || '—'}</td>
                    <td style={s.td}>{fmtDate(req.createdAt)}</td>
                    <td style={s.td}>{fmtDate(req.expiresAt)}</td>
                    <td style={s.td} onClick={e => e.stopPropagation()}>
                      {req.status === 'PENDING_APPROVAL' && (
                        <div style={{ display: 'flex', gap: 6 }}>
                          <button
                            type="button"
                            title="Approve"
                            style={{ ...s.btn('success'), padding: '6px 10px' }}
                            onClick={() => { setSelected(req); }}
                          >
                            Review
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {selected && (
        <DetailModal
          request={selected}
          onClose={() => { if (!busy) setSelected(null); }}
          onApprove={requestApprove}
          onReject={requestReject}
          busy={busy}
        />
      )}

      {confirm && (
        <ConfirmDialog
          type={confirm.type}
          request={confirm.req}
          rejectReason={confirm.reason}
          busy={busy}
          onCancel={() => { if (!busy) setConfirm(null); }}
          onConfirm={() => {
            if (confirm.type === 'approve') handleApprove(confirm.req);
            else handleReject(confirm.req, confirm.reason);
          }}
        />
      )}
    </div>
  );
}
